import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, type Icon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useSidebarCollapsed } from '@/components/shell/sidebar-context';

// WizardDialog is the shared shell for the in-wizard "generate in the
// background" sub-flows (categories, schedule, website). It's a centered modal
// with a branded header; the body is whatever generator the caller renders.
// Matches the design system: rounded-2xl card, pill close button, sentence-case
// title, Phosphor icon with explicit size.
export function WizardDialog({
  open,
  onOpenChange,
  icon: Glyph,
  title,
  subtitle,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon: Icon;
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation('common');
  // Center over the CONTENT area (right of the desktop sidebar rail), not the
  // full viewport — shift right by half the rail width. Mirrors
  // GettingStartedDialog; base `left-1/2` covers mobile (no rail).
  const collapsed = useSidebarCollapsed();
  const lgOffset = collapsed ? 'lg:left-[calc(50%+36px)]' : 'lg:left-[calc(50%+130px)]';
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[95] bg-foreground/40" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={`fixed left-1/2 ${lgOffset} top-1/2 z-[96] flex max-h-[86vh] w-[min(42rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-xl`}
        >
          <header className="flex items-start gap-3 border-b border-border px-6 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15">
              <Glyph size={22} weight="fill" className="text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="font-heading text-lg font-bold leading-tight tracking-tight">
                {title}
              </DialogPrimitive.Title>
              {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
            </div>
            <DialogPrimitive.Close asChild>
              <Button type="button" variant="ghost" size="icon" className="shrink-0" aria-label={t('setupWizard.close')}>
                <X size={18} />
              </Button>
            </DialogPrimitive.Close>
          </header>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-5">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
